import React from "react";
import { IoCheckmarkSharp, IoCloseSharp } from "react-icons/io5";
import { BsBasket3 } from "react-icons/bs";
import { MdBlock } from "react-icons/md";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import EATooltip from "../EATooltip/EATooltip";
import EAButton from "../Buttons/EAButton";
import TimeSlotTooltip from "./TimeSlotTooltip";
import { TimeSlotProps, SlotTooltipData } from "./types";
import { formatTimeRange, calculateDuration, getStatusText, getAvailablePlacesCount, pluralMiejsc } from "./utils";
import "./style.scss";

const TimeSlot: React.FC<TimeSlotProps> = ({
  data,
  contextName,
  status,
  onClick,
  isFirstRow = false,
  disabled = false,
  showOccupancy = false,
  showTimeOfEntance = true,
  showTicketsPoolForStepByStepSale = false,
}) => {
  const timeRange = formatTimeRange(data.startTime, data.endTime, showTimeOfEntance);
  const duration = showTimeOfEntance ? calculateDuration(data.startTime, data.endTime) : undefined;
  const statusText = getStatusText(status);
  const availablePlaces = getAvailablePlacesCount(data.capacity, data.reserved);

  const tooltipData: SlotTooltipData = {
    contextName,
    timeRange,
    duration,
    status: statusText,
    occupancy: status !== "unavailable" && status !== "sale-closed" && showOccupancy ? `${data.reserved}/${data.capacity}` : undefined,
    additionalInfo: data.status,
    availablePlaces:
      status !== "unavailable" && status !== "sale-closed" && showTicketsPoolForStepByStepSale
        ? availablePlaces
        : undefined,
  };

  const getCaptionText = (): string => {
    if (!showTimeOfEntance && showTicketsPoolForStepByStepSale) {
      return `${data.startTime} (${availablePlaces} ${pluralMiejsc(availablePlaces)})`;
    }
    return timeRange;
  };

  const handleClick = () => {
    if (!disabled && onClick && status === "available") {
      onClick(data, contextName);
    }
  };

  const tooltipPosition = isFirstRow ? "bottom" : "top";

  // Sale closed slot
  if (status === "sale-closed") {
    const now = Date.now();
    let saleMessage = "";

    // Jeśli mamy saleTo, to sprzedaż się skończyła
    if (data.saleTo) {
      if (now >= data.saleTo) {
        saleMessage = `Sprzedaż do ${format(new Date(data.saleTo), "dd.MM.yyyy HH:mm", { locale: pl })}`;
      }
    }

    return (
      <EATooltip content={<TimeSlotTooltip data={tooltipData} />} position={tooltipPosition} fullWidth>
        <div className="time-slot time-slot--sale-closed">
          <MdBlock className="time-slot__icon" />
          <span>Niedost. w sprzedaży</span>
          {saleMessage && <div className="time-slot__caption">{saleMessage}</div>}
        </div>
      </EATooltip>
    );
  }

  // Unavailable slot
  if (status === "unavailable") {
    return (
      <EATooltip content={<TimeSlotTooltip data={tooltipData} />} position={tooltipPosition} fullWidth>
        <div className="time-slot time-slot--unavailable">
          <MdBlock className="time-slot__icon" />
          <span>Niedostępne</span>
        </div>
      </EATooltip>
    );
  }

  // Assigned slot
  if (status === "assigned") {
    return (
      <EATooltip content={<TimeSlotTooltip data={tooltipData} />} position={tooltipPosition} fullWidth>
        <div className="time-slot time-slot--assigned">
          <IoCheckmarkSharp className="time-slot__icon" />
          <span>Jesteś zapisany</span>
        </div>
      </EATooltip>
    );
  }

  // In basket slot
  if (status === "in-basket") {
    return (
      <EATooltip content={<TimeSlotTooltip data={tooltipData} />} position={tooltipPosition} fullWidth>
        <div className="time-slot time-slot--in-basket">
          <BsBasket3 className="time-slot__icon" />
          <span>W koszyku</span>
        </div>
      </EATooltip>
    );
  }

  // Sold out slot
  if (status === "sold-out") {
    return (
      <EATooltip content={<TimeSlotTooltip data={tooltipData} />} position={tooltipPosition} fullWidth>
        <div className="time-slot time-slot--sold-out">
          <IoCloseSharp className="time-slot__icon" />
          <span>Wyprzedane</span>
        </div>
      </EATooltip>
    );
  }

  // Available slot
  return (
    <EATooltip content={<TimeSlotTooltip data={tooltipData} />} position={tooltipPosition} fullWidth>
      <div className="time-slot time-slot--available">
        <EAButton
          title={`Rezerwuj `}
          variant="filled"
          color="primary"
          onClick={handleClick}
          fullWidth
          noMargins
          disabled={disabled}
        />
        <div className="time-slot__caption">{getCaptionText()}</div>
      </div>
    </EATooltip>
  );
};

export default TimeSlot;
